// src/hooks/useInfinitePosts.js
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
} from 'firebase/firestore';
import { db } from '@root/firebase';

export default function useInfinitePosts({ pageSize = 10 } = {}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [eof, setEof] = useState(false);

  // internal guards
  const fetchingRef = useRef(false);
  const lastDocRef = useRef(null);
  const seenIdsRef = useRef(new Set());

  const fetchNext = useCallback(async () => {
    if (fetchingRef.current || eof) return;
    fetchingRef.current = true;
    setLoading(true);

    try {
      let q = query(
        collection(db, 'posts'),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      );
      if (lastDocRef.current) {
        q = query(
          collection(db, 'posts'),
          orderBy('createdAt', 'desc'),
          startAfter(lastDocRef.current),
          limit(pageSize)
        );
      }

      const snap = await getDocs(q);
      if (snap.empty) {
        setEof(true);
        return;
      }

      // append new, de-duped
      const next = [];
      snap.forEach((docSnap) => {
        const id = docSnap.id;
        if (!seenIdsRef.current.has(id)) {
          seenIdsRef.current.add(id);
          next.push({ id, ...docSnap.data() });
        }
      });

      // if all were dupes, try moving cursor but don’t re-run automatically
      lastDocRef.current =
        snap.docs[snap.docs.length - 1] || lastDocRef.current;

      setItems((prev) => [...prev, ...next]);
      if (next.length === 0 && snap.size < pageSize) {
        // nothing new came through + batch smaller than pageSize → likely EOF
        setEof(true);
      }
    } finally {
      fetchingRef.current = false;
      setLoading(false);
    }
  }, [pageSize, eof]);

  // IntersectionObserver
  const sentinelRef = useRef(null);
  const observerRef = useRef(null);

  const setSentinel = useCallback(
    (node) => {
      sentinelRef.current = node;
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (!node) return;
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry?.isIntersecting && !fetchingRef.current && !eof) {
            fetchNext();
          }
        },
        { root: null, rootMargin: '600px 0px', threshold: 0 }
      );
      observerRef.current.observe(node);
    },
    [fetchNext, eof]
  );

  // initial load (once)
  useEffect(() => {
    // don’t rely on StrictMode double-invoke; guard with ref
    if (!fetchingRef.current && items.length === 0) {
      fetchNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { items, loading, eof, sentinelRef: setSentinel, fetchNext };
}
